import { useEffect, useRef, useState } from 'react'
import { getAssetUrl } from '../utils'
import { AudioPlayer, type AudioPlayerHandle } from './AudioPlayer'
import type { PointOfInterest } from './PointOfInterest'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { ASSETS } from '../assets'
import { audio } from '../utils/audio'
import type ExperienceData from '../types/experience'

interface Props {
  pointOfInterests: PointOfInterest[]
  activePOI: number
  isAnimating: boolean
  onClose: () => void
  experience: ExperienceData
}
export default function RightDrawer({
  pointOfInterests,
  activePOI,
  isAnimating,
  onClose,
  experience,
}: Props) {
  const [activeTab, setActiveTab] = useState<number>(1)
  const [tabClosing, setTabClosing] = useState<boolean>(false)
  const [open, setOpen] = useState(activePOI && activePOI !== 0)
  const drawerRef = useRef<HTMLDivElement>(null)
  const bg1Ref = useRef<HTMLDivElement>(null)
  const bg2Ref = useRef<HTMLDivElement>(null)
  const audioPlayerRef = useRef<AudioPlayerHandle>(null)

  useEffect(() => {
    audioPlayerRef.current?.stopAudio()
  }, [activeTab])

  useEffect(() => {
    const sfx = experience?.soundEffects?.tabs
    if (!sfx) return
    const url = sfx.url.startsWith('data:') ? sfx.url : getAssetUrl(sfx.filename)
    if (!url) return
    audio.register({ tabs: url })
  }, [experience?.soundEffects?.tabs])

  useEffect(() => {
    if (isAnimating) return
    setOpen(activePOI && activePOI !== 0)
  }, [activePOI, isAnimating])

  useGSAP(
    () => {
      if (open) {
        // Open Drawer
        gsap.to(drawerRef.current, {
          x: 0,
          duration: 1,
          ease: 'power3.out',
          onStart: () => {
            if (bg1Ref.current) bg1Ref.current.style.display = 'block'
            if (bg2Ref.current) bg2Ref.current.style.display = 'none'
          },
          onComplete: () => {
            if (bg1Ref.current) bg1Ref.current.style.display = 'none'
            if (bg2Ref.current) bg2Ref.current.style.display = 'block'
          },
        })
      } else {
        // Close Drawer
        gsap.to(drawerRef.current, {
          x: '100%',
          duration: 0.5,
          ease: 'power3.in',
          onStart: () => {
            if (bg2Ref.current) bg2Ref.current.style.display = 'none'
            if (bg1Ref.current) bg1Ref.current.style.display = 'block'
          },
          onComplete: () => {
            onClose()
            setTabClosing(false)
          },
        })
      }
    },
    { dependencies: [open] }
  )

  const handleClose = () => {
    if (isAnimating || tabClosing) return
    setTabClosing(true)
    audio.play('closeButton')
    // Close Drawer
    gsap.to(drawerRef.current, {
      x: '100%',
      duration: 0.5,
      ease: 'power3.in',
      onStart: () => {
        if (bg2Ref.current) bg2Ref.current.style.display = 'none'
        if (bg1Ref.current) bg1Ref.current.style.display = 'block'
      },
      onComplete: () => {
        onClose()
        setTabClosing(false)
      },
    })
  }
  return (
    <>
      <div ref={bg1Ref} className="absolute inset-0 z-10 bg-[#001D6CAA]"></div>
      <div ref={bg2Ref} className="absolute inset-0 z-10 w-[36vw] bg-[#001D6CAA]"></div>
      <div
        ref={drawerRef}
        className="absolute inset-0 z-11 flex justify-end"
        style={{ transform: 'translateX(100%)' }}
        onClick={handleClose}
      >
        {/* Drawer panel */}
        <div className="absolute z-12 h-full w-[64vw] bg-[#000000A6]"></div>
        <div
          className="relative z-20 flex h-full w-[64vw] flex-col justify-end rounded-lg bg-[#001D6CAA] p-10 text-white"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex h-3/10 w-full items-start justify-end">
            <div
              className="group border-blue hover:border-blue-tint-3 active:border-blue-tint-3 flex flex-shrink-0 items-center justify-center rounded-full border-3 bg-transparent hover:cursor-pointer"
              style={{ width: 65, height: 65 }}
              onClick={handleClose}
            >
              <div
                className="bg-blue group-hover:bg-blue-tint-3 group-active:bg-blue-tint-3 flex flex-shrink-0 items-center justify-center rounded-full"
                style={{ width: 50, height: 50 }}
              >
                <img src={ASSETS.iconCross} width={17} height={17} className="rotate-45" />
              </div>
            </div>
          </div>

          {(() => {
            const activePOIData = pointOfInterests.find(poi => poi.id === activePOI)
            if (!activePOIData) return null

            const activeTabKey = `infoTab${activeTab}` as keyof typeof activePOIData.infoPanel
            const activeTabData = activePOIData.infoPanel[activeTabKey]

            if (
              !activeTabData ||
              typeof activeTabData === 'string' ||
              typeof activeTabData === 'number'
            ) {
              return null
            }

            return (
              <div className="flex h-7/10 flex-col bg-transparent">
                {/* Tab Content */}
                <div className="flex h-4/5 flex-col gap-10">
                  <div
                    className="border-purple-tint-1 flex flex-shrink-0 items-center justify-center rounded-full border-3 bg-transparent"
                    style={{ width: 65, height: 65 }}
                  >
                    <div
                      className="bg-purple flex flex-shrink-0 items-center justify-center rounded-full"
                      style={{ width: 50, height: 50 }}
                    >
                      <img src={ASSETS.iconCross} width={17} height={17} />
                    </div>
                  </div>
                  <h1 className="w-[40%] text-[28px] leading-none font-light">
                    {activeTabData.title}
                  </h1>
                  <div className="flex items-start gap-10 text-left text-[20px] leading-[27px] font-light">
                    <p className="w-1/2">{activeTabData.bodyColumn1}</p>
                    <p className="w-1/2">{activeTabData.bodyColumn2}</p>
                  </div>
                </div>

                <div className="flex h-1/5 w-full">
                  {/* Tab Navigation */}
                  <div className="m-auto flex w-4/5 items-stretch gap-1 self-start bg-transparent">
                    {[1, 2, 3].map(tabNum => {
                      const tabKey = `infoTab${tabNum}` as keyof typeof activePOIData.infoPanel
                      const tabData = activePOIData.infoPanel[tabKey]

                      if (!tabData || typeof tabData === 'string' || typeof tabData === 'number') {
                        return null
                      }

                      return (
                        <button
                          key={tabNum}
                          onClick={() => {
                            // Ensure tabs SFX is registered right before play (belt-and-suspenders)
                            const sfx = experience?.soundEffects?.tabs
                            if (sfx) {
                              const url = sfx.url.startsWith('data:')
                                ? sfx.url
                                : getAssetUrl(sfx.filename)
                              if (url) {
                                audio.register({ tabs: url })
                              }
                            }
                            audio.play('tabs', { volume: 3 })
                            setActiveTab(tabNum)
                          }}
                          className={`flex flex-1 items-center gap-15 py-3 pr-5 pl-4 text-left text-[20px] leading-[27px] font-light transition-colors ${
                            activeTab === tabNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue text-gray-300 hover:bg-blue-600 active:bg-blue-600'
                          }`}
                        >
                          {tabData.tabName}
                          <img src={ASSETS.iconArrowRight} width={24} height={15} />
                        </button>
                      )
                    })}
                  </div>
                  {activeTabData.audio && (
                    <div className="flex w-1/5 items-center justify-end">
                      <AudioPlayer
                        ref={audioPlayerRef}
                        audioUrl={
                          activeTabData.audio.url.startsWith('data:')
                            ? activeTabData.audio.url
                            : getAssetUrl(activeTabData.audio.filename)
                        }
                        className="h-[83px] w-[83px] bg-blue-600 hover:bg-blue-700"
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          })()}
        </div>
      </div>
    </>
  )
}
